import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Eye, Hand, Users, ShieldOff, Sparkles, Wind, MessageCircle } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';

// ── Reusing images already in the project (placeholders for real school photos) ──
const IMG_CLASSROOM = 'https://images.unsplash.com/photo-1777056481869-feac70afe522?w=900&q=85';
const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20booking%20a%20school%20tour%20at%20Sri%20Aurobindo%20Montessori%20School';

const sixThings = [
  {
    icon: <Wind size={24} className="text-[#1E3A8A]" />,
    title: 'Freedom within limits',
    desc: 'Each child chooses their activity, works at their own pace, repeats it as often as they wish, and is never interrupted mid-concentration. Guides step in for only three reasons: a child hurting themselves, hurting another, or damaging the material. Everything else belongs to the child.',
  },
  {
    icon: <Eye size={24} className="text-[#1E3A8A]" />,
    title: 'The teacher as guide, not lecturer',
    desc: 'Roughly 98% of the teaching lives in the scientifically designed materials; the guide\'s art is knowing which material which child is ready for, when. The teacher follows the child — never the other way round.',
  },
  {
    icon: <Hand size={24} className="text-[#1E3A8A]" />,
    title: 'Hands-on materials',
    desc: '"Hands feed the mind," said Dr. Montessori. Numbers learned through beads you can hold. Geography through maps you can trace. Grammar through objects you can move. Understanding built by the hand does not fade.',
  },
  {
    icon: <Users size={24} className="text-[#1E3A8A]" />,
    title: 'Mixed ages, real community',
    desc: 'Younger children are inspired by watching older ones; older children cement their learning by helping juniors — and run to do it. Leadership and kindness are practised daily, not taught in a moral science period.',
  },
  {
    icon: <ShieldOff size={24} className="text-[#1E3A8A]" />,
    title: 'No tests, no homework, no comparison',
    desc: 'Mastery is visible in the child\'s work itself. Without ranks and red marks, children never learn to fear learning — and never measure their worth against the child at the next desk.',
  },
  {
    icon: <Sparkles size={24} className="text-[#1E3A8A]" />,
    title: 'A prepared environment',
    desc: 'Every material has a designated, beautiful place, at child height, cared for by the children themselves. Order outside builds order within. A child absorbs a calm, organised room as surely as she absorbs language.',
  },
];

const comparisonRows = [
  { theme: 'Who sets the pace', traditional: 'The teacher and the syllabus', montessori: 'The child, guided by trained observation' },
  { theme: 'How learning happens', traditional: 'Blackboard → notebook, mostly listening', montessori: 'Hands-on materials, all senses engaged' },
  { theme: 'Class structure', traditional: '25–40 same-aged children, one teacher', montessori: 'Mixed ages; 1-to-1 presentations till 6, groups of 4–5 after' },
  { theme: 'Motivation', traditional: 'Tests, homework, marks, comparison', montessori: "The child's own interest and freely chosen repetition" },
  { theme: 'Writing & reading', traditional: 'Taught by copying, on a fixed schedule', montessori: 'Prepared indirectly from 2.5 — writing arrives naturally around 4, in cursive' },
  { theme: 'Speaking', traditional: 'Rarely practised individually', montessori: 'Show & Tell and public speaking from age 2.5' },
  { theme: 'Discipline', traditional: 'Imposed from outside', montessori: 'Grows from within, through concentration and purposeful work' },
  { theme: 'The result', traditional: 'Children who wait for instructions', montessori: 'Children who decide and implement' },
];

const accordions = [
  {
    value: 'absorbent-mind',
    title: 'The Absorbent Mind — why "give the world to the child by six"',
    content: (
      <p className="text-slate-600 leading-relaxed">
        From birth to six, a child's mind absorbs everything around it — language, habits, mannerisms, attitudes — effortlessly, the way a sponge absorbs water. Dr. Montessori observed that this extraordinary absorbing power actually <strong>recedes with age</strong>. That is why we say: give the world to the child by six. Not "finish the alphabet early" — but offer the whole world: stories of the universe, the evolution of humankind, maps and continents, music, courtesy, two or three languages, real practical skills. What is absorbed in these years becomes the rock-solid foundation of the person. A child here effectively completes the equivalent of Class 1 by age six — without a single test — but the foundation goes far deeper than academics: food habits, self-care, courtesy, and above all, a mind tuned to think for itself.
      </p>
    ),
  },
  {
    value: 'sensitive-periods',
    title: 'Sensitive periods — why timing matters more than hurry',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Children pass through windows when a particular ability develops almost effortlessly — order, movement, language, and later specific doorways such as mathematics (around 3.5) and writing (around 4). Montessori education works with these windows instead of against them. Example: writing needs three strong fingers to hold a pencil — so from 2.5, a child's hand is quietly strengthened through pouring, spooning, knobbed cylinders and metal insets. By four, writing simply <em>arrives</em>, and it arrives in cursive. Meanwhile a neighbour's child may be "writing earlier" by rote copying — one of the commonest misunderstandings about Montessori. We are not behind; we are building the hand, the mind and the desire first, so the skill lasts.
      </p>
    ),
  },
  {
    value: 'planes-of-development',
    title: 'The planes of development — why one method spans 2 to 12',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Dr. Montessori mapped childhood in planes: 0–6, 6–12, 12–18. In the first plane, the child absorbs the world and builds independence — <em>"help me do it myself."</em> In the second plane, the same child transforms: family is no longer world enough; they crave peers, group work, big questions, and reasons — <em>"help me think for myself."</em> Our Toddler and Primary environments serve the first plane; our Elementary serves the second, with small groups, great stories of the universe, field trips, and the gradual move from concrete materials into pure abstraction. One philosophy, tuned precisely to each stage.
      </p>
    ),
  },
  {
    value: 'freedom-discipline',
    title: 'Freedom is not the absence of discipline',
    content: (
      <p className="text-slate-600 leading-relaxed">
        The freedom here has clear, firm limits — care of self, care of others, care of the environment. Within those limits, the child's will, mind and body work in harmony: he wills an activity, decides to take it, and does it with his hands. That synchronisation is where deep concentration comes from — and out of concentration grows the truest discipline: the kind that <strong>comes from within</strong> and needs no bell or scolding.
      </p>
    ),
  },
  {
    value: 'further-reading',
    title: 'Want to read what we read?',
    content: (
      <div className="text-slate-600 leading-relaxed space-y-3">
        <p>Start with Dr. Montessori's own books — <em>The Secret of Childhood</em> and <em>The Absorbent Mind</em> are the two we recommend to every parent.</p>
        <p>Then: <em>The Child in the Family, The Montessori Method, The Discovery of the Child, To Educate the Human Potential,</em> and <em>From Childhood to Adolescence.</em></p>
        <p>Ask us on your visit — we love talking about these.</p>
      </div>
    ),
  },
];

export default function Montessori() {
  return (
    <div className="pt-16">
      <Helmet>
        <title>The Montessori Method, Simply Explained | Sri Aurobindo Montessori, Hyderabad</title>
        <meta name="description" content="What is Montessori, and how is it different from CBSE/ICSE-style schooling? Freedom within limits, the absorbent mind, hands-on materials, mixed-age classrooms — explained simply for parents." />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Our Way</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Montessori, explained<br className="hidden sm:block" /> the way we'd explain it<br className="hidden sm:block" /> to a friend
          </h1>
          <p className="text-blue-200 text-base max-w-xl mx-auto">
            No jargon. No brochure-speak. Just the honest picture.
          </p>
        </div>
      </section>

      {/* ── THE IDEA IN ONE MINUTE ── */}
      <section className="py-20 bg-white" data-testid="idea-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">The Idea</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-8 leading-tight" style={{ fontFamily: 'Nunito, sans-serif' }}>
                The idea in one minute
              </h2>
              <div className="space-y-5 text-slate-600 leading-relaxed text-base">
                <p>
                  Dr. Maria Montessori — physician, psychologist, and Italy's first woman doctor — spent her life scientifically observing how children actually learn. Her discovery, now over a century old and continuously refined by AMI's research, is beautifully simple:
                </p>
                <div className="bg-[#EFF6FF] rounded-3xl px-8 py-6 border-l-4 border-[#1E3A8A]">
                  <p className="text-[#1E3A8A] font-bold text-base leading-relaxed">
                    Children are born wanting to learn. Give them freedom, the right materials, and a trained adult who observes more than instructs — and they teach themselves, joyfully.
                  </p>
                </div>
                <p>
                  Our founder puts it in four words: <strong className="text-[#1E3A8A]">Make the child independent.</strong>
                </p>
                <p>
                  A child who chooses her own work, concentrates deeply on it, repeats it until she masters it, and solves her own problems is quietly building something no test can measure: the habit of deciding and doing. That habit becomes confidence, and that confidence lasts a lifetime.
                </p>
              </div>
            </div>

            {/* Placeholder image */}
            <div className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.12)]">
              <img
                src={IMG_CLASSROOM}
                alt="Children in the Montessori environment — placeholder for school photo"
                className="w-full h-[420px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── SIX THINGS ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="six-things-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">What You'll See</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Six things you'll see in<br className="hidden sm:block" /> every environment here
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sixThings.map((item, i) => (
              <div
                key={i}
                data-testid={`six-thing-${i}`}
                className="bg-white rounded-3xl p-8 shadow-[0_4px_20px_rgba(59,130,246,0.07)] hover:shadow-[0_12px_40px_rgba(59,130,246,0.12)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="w-11 h-11 bg-[#EFF6FF] rounded-2xl flex items-center justify-center mb-5 flex-shrink-0">
                  {item.icon}
                </div>
                <h3 className="text-base font-extrabold text-[#0F172A] mb-3 leading-snug" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="py-20 bg-white" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">An Honest Look</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A] mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Montessori vs. the traditional classroom
            </h2>
            <p className="text-slate-600 leading-relaxed max-w-3xl">
              Many of our parents also have older children in excellent CBSE/ICSE schools. This isn't about "bad vs. good" — it's about what each system optimises for, especially before age six.
            </p>
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block rounded-3xl overflow-hidden shadow-[0_4px_30px_rgba(30,58,138,0.08)] border border-blue-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#1E3A8A]">
                  <th className="bg-[#F4F7FB] text-[#1E3A8A] font-extrabold px-6 py-4 text-left w-1/3 border-r border-blue-100">Theme</th>
                  <th className="bg-[#F4F7FB] text-[#1E3A8A] font-extrabold px-6 py-4 text-left border-r border-blue-100">Traditional classroom</th>
                  <th className="bg-[#1E3A8A] text-white font-bold px-6 py-4 text-left">Montessori at Aurobindo</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9FBFF]'}>
                    <td className="px-6 py-4 font-semibold text-[#0F172A] border-b border-blue-50">{row.theme}</td>
                    <td className="px-6 py-4 text-slate-500 border-b border-blue-50">{row.traditional}</td>
                    <td className="px-6 py-4 text-[#1E3A8A] font-medium border-b border-blue-50">{row.montessori}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="sm:hidden space-y-4">
            {comparisonRows.map((row, i) => (
              <div key={i} data-testid={`comparison-row-${i}`} className="bg-[#F4F7FB] rounded-2xl overflow-hidden">
                <div className="px-5 py-3 bg-[#1E3A8A]/10">
                  <p className="font-bold text-[#1E3A8A] text-xs uppercase tracking-wide">{row.theme}</p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-blue-100">
                  <div className="px-4 py-4">
                    <p className="text-xs font-bold text-slate-400 mb-1 uppercase tracking-wide">Traditional</p>
                    <p className="text-slate-600 text-xs leading-relaxed">{row.traditional}</p>
                  </div>
                  <div className="px-4 py-4 bg-[#EFF6FF]">
                    <p className="text-xs font-bold text-[#60A5FA] mb-1 uppercase tracking-wide">Montessori</p>
                    <p className="text-[#1E3A8A] text-xs font-medium leading-relaxed">{row.montessori}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Anecdote */}
          <div className="mt-10 bg-[#EFF6FF] rounded-3xl px-8 py-7 border-l-4 border-[#1E3A8A]">
            <p className="text-slate-700 text-sm leading-relaxed italic">
              <strong className="text-[#1E3A8A] not-italic">A small story our parents love:</strong> A Primary child working with the "land and water forms" material sat down to lunch, placed his idli in the middle of his sambar and announced: "Island!" Then sambar in the middle of idli: "Lake!" That is Montessori — the classroom and the world are the same place.
            </p>
          </div>
        </div>
      </section>

      {/* ── ACCORDIONS ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="accordion-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Go Deeper</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              For the curious: the deeper "why"
            </h2>
            <p className="text-slate-500 text-sm mt-3">Tap any topic to expand — these are for parents who want the full picture.</p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {accordions.map((item) => (
              <AccordionItem
                key={item.value}
                value={item.value}
                data-testid={`accordion-${item.value}`}
                className="bg-white rounded-2xl border border-blue-50 shadow-[0_2px_12px_rgba(59,130,246,0.06)] px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-[#1E3A8A] font-bold text-sm py-5 hover:no-underline hover:text-[#1E40AF] text-left">
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-5">
                  {item.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── SEE IT WITH YOUR OWN EYES ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="see-it-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Come Visit</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            See it with your own eyes
          </h2>
          <p className="text-blue-200 text-base leading-relaxed mb-10 max-w-xl mx-auto">
            Reading about concentration is one thing. Watching a three-year-old polish a mirror with total absorption for twenty unbroken minutes is another.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="montessori-tour-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a School Tour <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="montessori-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>WhatsApp</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
